/**
 * Live Display Page
 * Full-screen projection view for church services.
 * Display renderers are in ../components/LiveDisplay/ folder.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import WelcomeDisplay from '../components/LiveDisplay/WelcomeDisplay';
import HymnDisplay from '../components/LiveDisplay/HymnDisplay';
import BibleDisplay from '../components/LiveDisplay/BibleDisplay';
import AnnouncementDisplay from '../components/LiveDisplay/AnnouncementDisplay';
import OfferingDisplay from '../components/LiveDisplay/OfferingDisplay';
import PreServiceDisplay from '../components/LiveDisplay/PreServiceDisplay';
import BlankDisplay from '../components/LiveDisplay/BlankDisplay';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LiveDisplay = () => {
  const [display, setDisplay] = useState({ display_type: 'welcome', content: {} });
  const [settings, setSettings] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Load settings
  useEffect(() => {
    fetch(`${API}/api/display/settings`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(console.error);
  }, []);

  // Show/hide controls on mouse move
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // WebSocket connection + fallback polling
  useEffect(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${new URL(API).host}/api/display/live`;

    const connectWebSocket = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setDisplay(data);
          } catch (e) { console.error('WS parse error:', e); }
        };
        wsRef.current.onclose = () => {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
        };
        wsRef.current.onerror = () => {};
      } catch (e) { console.error('WS connection error:', e); }
    };

    connectWebSocket();

    const pollInterval = setInterval(() => {
      fetch(`${API}/api/display/current`)
        .then(res => res.json())
        .then(data => setDisplay(data))
        .catch(console.error);
    }, 2000);

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pollInterval);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const renderDisplay = () => {
    switch (display.display_type) {
      case 'welcome': return <WelcomeDisplay content={display.content} settings={settings} />;
      case 'hymn': return <HymnDisplay content={display.content} settings={settings} />;
      case 'bible': return <BibleDisplay content={display.content} settings={settings} />;
      case 'announcement': return <AnnouncementDisplay content={display.content} settings={settings} />;
      case 'offering': return <OfferingDisplay content={display.content} settings={settings} />;
      case 'preservice': return <PreServiceDisplay content={display.content} settings={settings} />;
      case 'blank': return <BlankDisplay settings={settings} />;
      default: return <WelcomeDisplay content={display.content} settings={settings} />;
    }
  };

  return (
    <div ref={containerRef} className="fixed inset-0 bg-slate-900 cursor-none select-none overflow-hidden" onDoubleClick={toggleFullscreen}>
      <AnimatePresence mode="wait">
        <motion.div key={display.display_type + display.updated_at}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
          className="w-full h-full">
          {renderDisplay()}
        </motion.div>
      </AnimatePresence>

      {/* Fullscreen Button */}
      <AnimatePresence>
        {showControls && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute top-4 right-4 z-50">
            <button onClick={toggleFullscreen}
              className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-xl backdrop-blur-sm transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
              {isFullscreen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logo Watermark */}
      {(settings?.logo_url || '/images/church-logo.png?v=2') && settings?.logo_position === 'watermark' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ opacity: settings?.logo_opacity || 0.15 }}>
          <img src={settings?.logo_url || '/images/church-logo.png?v=2'} alt="" className="w-1/3 max-w-md object-contain" />
        </div>
      )}

      {/* Corner Logo */}
      {(settings?.logo_url || '/images/church-logo.png?v=2') && settings?.logo_position && settings?.logo_position !== 'watermark' && (
        <div className={`absolute pointer-events-none ${
          settings.logo_position === 'top-left' ? 'top-4 left-4' :
          settings.logo_position === 'top-right' ? 'top-4 right-4' :
          settings.logo_position === 'bottom-left' ? 'bottom-4 left-4' : 'bottom-4 right-4'
        }`} style={{ opacity: settings?.logo_opacity || 0.5 }}>
          <img src={settings?.logo_url || '/images/church-logo.png?v=2'} alt="" className="w-16 h-16 object-contain" />
        </div>
      )}
    </div>
  );
};

export default LiveDisplay;
